import {
  selectOrganizationId,
  useCreateWorkspaceMutation,
} from '@carboncopyco/data';
import { PlusOutlineIcon, VOutlineIcon } from '@carboncopyco/icons';
import type { Workspace } from '@carboncopyco/types';
import {
  ColorHex,
  Dropdown,
  DropdownItem,
  Separator,
  TextBase,
  TextCaption,
  notify,
} from '@carboncopyco/ui';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';
import { useField } from 'formik';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { WorkspaceSettingsModal } from '../WorkspaceSettingsModal';
import { WorkspaceItem } from './WorkspaceItem';
import { WorkspaceSelectInput } from './WorkspaceSelectInput';

interface WorkspaceSelectionFieldProps {
  workspaces: Workspace[];
  label: string;
  name: string;
  disabled?: boolean;
  width?: number;
  location?: string;
}

export const WorkspaceSelectionField = ({
  workspaces,
  label,
  name,
  disabled,
  width = 300,
  location = '',
}: WorkspaceSelectionFieldProps) => {
  const organizationId = useSelector(selectOrganizationId);
  const [field, , helpers] = useField({
    name,
    type: 'number',
  });
  const selectedWorkspace = useMemo(
    () => workspaces.find(({ id }) => id === field.value),
    [field.value, workspaces]
  );
  const [createWorkspaceModalVisible, createWorkspaceModal] = useBoolean();
  const [dropdownVisible, dropdown] = useBoolean();
  const [createWorkspace, { isLoading: isCreatingWorkspace }] =
    useCreateWorkspaceMutation();
  const { trackWorkspaceClickOpenCreateModal, trackWorkspaceClickCreate } =
    useWorkspaceAnalytics(location);

  return (
    <>
      <div>
        <label htmlFor={field.name}>
          <TextCaption
            label={label}
            color="text-ds-natural-300"
          />
        </label>
        <Dropdown
          open={dropdownVisible}
          onOpenChange={dropdown.toggle}
          disabled={disabled}
          scroll={workspaces?.length > 6}
          contentClass="pb-11"
          triggerElement={
            <div style={{ width }}>
              <WorkspaceSelectInput
                disabled={disabled}
                workspace={selectedWorkspace}
              />
            </div>
          }
          side="bottom"
          align="center"
          width={width}
        >
          {workspaces?.map((workspace) => (
            <DropdownItem
              onClick={() => helpers.setValue(workspace.id)}
              key={workspace.id}
            >
              <div className="flex items-center justify-between py-2.5 px-2 cursor-pointer">
                <WorkspaceItem workspace={workspace} />
                {field.value === workspace.id && (
                  <VOutlineIcon
                    size={24}
                    color={ColorHex.natural300}
                  />
                )}
              </div>
            </DropdownItem>
          ))}
          <div
            style={{ width: 'calc(100% - 15px)' }}
            onClick={() => {
              dropdown.setFalse();
              createWorkspaceModal.setTrue();
              trackWorkspaceClickOpenCreateModal();
            }}
            className="flex flex-col items-center justify-between  px-2 rounded-bl-md cursor-pointer fixed bottom-0 left-0 bg-ds-accent3-100 hover:bg-ds-natural-50"
          >
            <Separator horizontal />
            <div className="flex items-center gap-1.5 w-full py-2.5">
              <PlusOutlineIcon color={ColorHex.primary300} />
              <TextBase
                label="Create new"
                color="text-ds-primary-300"
              />
            </div>
          </div>
        </Dropdown>
      </div>
      <WorkspaceSettingsModal
        isNew
        isOpen={createWorkspaceModalVisible}
        zIndex={9999}
        handleSubmit={(settings) => {
          createWorkspace({ organizationId, ...settings })
            .unwrap()
            .then((res) => {
              helpers.setValue(res.id);
              notify.success(
                `Created workspace ${res.workspaceName} successfully.`
              );
              trackWorkspaceClickCreate({ workspace: res });
            })
            .catch(() => notify.error('Error creating new workspace.'))
            .finally(createWorkspaceModal.setFalse);
        }}
        handleClose={createWorkspaceModal.setFalse}
        isLoading={isCreatingWorkspace}
      />
    </>
  );
};
